import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { usePermissions } from '../context/PermissionContext';

interface Supplier {
  id: number;
  company_name: string;
  contact_person?: string;
  phone?: string;
  email?: string;
  country?: string;
  city?: string;
  address?: string;
  currency: string;
  payment_terms: string;
  inn?: string;
  bank_name?: string;
  bank_account?: string;
  swift?: string;
  notes?: string;
  is_active: number;
}

const currencyLabels: Record<string, string> = { uzs: "UZS", usd: "USD", eur: "EUR" };
const paymentLabels: Record<string, string> = {
  prepayment: "Oldindan to'lov", net15: "Net 15", net30: "Net 30", net60: "Net 60", net90: "Net 90",
};

const emptyForm = {
  company_name: '', contact_person: '', phone: '', email: '',
  country: '', city: '', address: '',
  currency: 'usd', payment_terms: 'prepayment',
  inn: '', bank_name: '', bank_account: '', swift: '', notes: '',
};

const Suppliers: React.FC = () => {
  const { canEdit } = usePermissions();
  const canFull = canEdit('suppliers');
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState<Supplier | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    try { const res = await api.get('/suppliers'); setSuppliers(res.data); }
    catch (e) { console.error(e); }
  };
  useEffect(() => { fetchData(); }, []);

  const filtered = suppliers.filter(s => {
    const matchSearch = s.company_name.toLowerCase().includes(search.toLowerCase()) ||
      (s.contact_person || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.country || '').toLowerCase().includes(search.toLowerCase());
    const matchActive = activeOnly ? s.is_active === 1 : true;
    return matchSearch && matchActive;
  });

  const openAdd = () => { setForm(emptyForm); setEditId(null); setError(''); setShowModal(true); };
  const openEdit = (s: Supplier) => {
    setForm({ company_name: s.company_name, contact_person: s.contact_person || '', phone: s.phone || '',
      email: s.email || '', country: s.country || '', city: s.city || '', address: s.address || '',
      currency: s.currency, payment_terms: s.payment_terms,
      inn: s.inn || '', bank_name: s.bank_name || '', bank_account: s.bank_account || '', swift: s.swift || '',
      notes: s.notes || '' });
    setEditId(s.id); setError(''); setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.company_name.trim()) { setError("Kompaniya nomi majburiy"); return; }
    setLoading(true); setError('');
    try {
      if (editId) await api.put(`/suppliers/${editId}`, form);
      else await api.post('/suppliers', form);
      setShowModal(false); fetchData();
    } catch (e: any) { setError(e.response?.data?.detail || 'Xatolik'); }
    finally { setLoading(false); }
  };

  const handleToggleActive = async (s: Supplier) => {
    if (!canFull) return;
    await api.put(`/suppliers/${s.id}`, { is_active: s.is_active === 1 ? 0 : 1 });
    fetchData();
  };

  const handleDelete = async (id: number) => {
    if (!canFull) return;
    if (!window.confirm("O'chirishni tasdiqlaysizmi?")) return;
    await api.delete(`/suppliers/${id}`); fetchData();
  };

  const f = (key: string, val: string) => setForm({ ...form, [key]: val });

  return (
    <div className="p-3 md:p-6">
      <div className="flex justify-between items-center mb-4 md:mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800">Yetkazib beruvchilar</h2>
        <button onClick={() => canFull && openAdd()} disabled={!canFull}
          className={`px-3 md:px-4 py-2 rounded-lg text-sm text-white ${canFull ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}>
          + Qo'shish
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 md:gap-4 mb-4 md:mb-6">
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <p className="text-gray-500 text-xs md:text-sm">Jami</p>
          <p className="text-xl md:text-2xl font-bold text-gray-800">{suppliers.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <p className="text-gray-500 text-xs md:text-sm">Faol</p>
          <p className="text-xl md:text-2xl font-bold text-green-600">{suppliers.filter(s => s.is_active).length}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <p className="text-gray-500 text-xs md:text-sm">Nofaol</p>
          <p className="text-xl md:text-2xl font-bold text-red-500">{suppliers.filter(s => !s.is_active).length}</p>
        </div>
      </div>

      {/* Filtr */}
      <div className="flex gap-2 mb-4">
        <input type="text" placeholder="Qidirish..." value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <label className="flex items-center gap-2 border rounded-lg px-3 py-2 text-sm cursor-pointer whitespace-nowrap">
          <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} />
          Faollar
        </label>
      </div>

      {/* Desktop jadval */}
      <div className="hidden md:block bg-white rounded-xl shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Kompaniya</th>
              <th className="px-4 py-3 text-left">Kontakt</th>
              <th className="px-4 py-3 text-left">Joylashuv</th>
              <th className="px-4 py-3 text-left">Valyuta</th>
              <th className="px-4 py-3 text-left">To'lov sharti</th>
              <th className="px-4 py-3 text-center">Holat</th>
              <th className="px-4 py-3 text-center">Amallar</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-gray-400">Yetkazib beruvchilar yo'q</td></tr>
            ) : filtered.map(s => (
              <tr key={s.id} className={`border-t hover:bg-gray-50 ${!s.is_active ? 'opacity-50' : ''}`}>
                <td className="px-4 py-3 font-medium">{s.company_name}</td>
                <td className="px-4 py-3">
                  <div>{s.contact_person || '—'}</div>
                  {s.phone && <div className="text-xs text-blue-500">{s.phone}</div>}
                </td>
                <td className="px-4 py-3">{s.country && s.city ? `${s.country}, ${s.city}` : s.country || s.city || '—'}</td>
                <td className="px-4 py-3"><span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">{currencyLabels[s.currency]}</span></td>
                <td className="px-4 py-3">{paymentLabels[s.payment_terms]}</td>
                <td className="px-4 py-3 text-center">
                  <button onClick={() => handleToggleActive(s)} disabled={!canFull}
                    className={`px-2 py-1 rounded-full text-xs font-medium ${!canFull ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : s.is_active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}>
                    {s.is_active ? 'Faol' : 'Nofaol'}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => setShowDetail(s)} className="px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-xs">👁️</button>
                    <button onClick={() => canFull && openEdit(s)} disabled={!canFull}
                      className={canFull ? "px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs" : "px-2 py-1 rounded text-xs bg-gray-100 text-gray-400 cursor-not-allowed"}>✏️</button>
                    <button onClick={() => handleDelete(s.id)} disabled={!canFull}
                      className={canFull ? "px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs" : "px-2 py-1 rounded text-xs bg-gray-100 text-gray-400 cursor-not-allowed"}>🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile kard */}
      <div className="md:hidden space-y-3">
        {filtered.length === 0 ? (
          <div className="text-center py-10 text-gray-400 bg-white rounded-xl shadow">Yetkazib beruvchilar yo'q</div>
        ) : filtered.map(s => (
          <div key={s.id} className={`bg-white rounded-xl shadow p-4 ${!s.is_active ? 'opacity-50' : ''}`}>
            <div className="flex justify-between items-start mb-2">
              <p className="font-bold text-gray-800">{s.company_name}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full ${s.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {s.is_active ? 'Faol' : 'Nofaol'}
              </span>
            </div>
            {s.contact_person && <p className="text-sm text-gray-600 mb-1">{s.contact_person}</p>}
            {s.phone && <p className="text-sm text-blue-500 mb-1">{s.phone}</p>}
            <div className="flex gap-2 text-xs text-gray-500">
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">{currencyLabels[s.currency]}</span>
              <span>{paymentLabels[s.payment_terms]}</span>
            </div>
            <div className="flex gap-2 mt-3 pt-3 border-t">
              <button onClick={() => setShowDetail(s)} className="flex-1 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs">Ko'rish</button>
              {canFull && <button onClick={() => openEdit(s)} className="flex-1 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs">Tahrirlash</button>}
              {canFull && <button onClick={() => handleDelete(s.id)} className="flex-1 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs">O'chirish</button>}
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-xl w-full md:max-w-2xl p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg md:text-xl font-bold mb-4">{editId ? 'Tahrirlash' : 'Yangi yetkazib beruvchi'}</h3>
            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Kompaniya nomi *</label>
                <input value={form.company_name} onChange={e => f('company_name', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kontakt shaxs</label>
                <input value={form.contact_person} onChange={e => f('contact_person', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                <input value={form.phone} onChange={e => f('phone', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={form.email} onChange={e => f('email', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mamlakat</label>
                <input value={form.country} onChange={e => f('country', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shahar</label>
                <input value={form.city} onChange={e => f('city', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valyuta</label>
                <select value={form.currency} onChange={e => f('currency', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="usd">USD</option>
                  <option value="eur">EUR</option>
                  <option value="uzs">UZS</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To'lov sharti</label>
                <select value={form.payment_terms} onChange={e => f('payment_terms', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {Object.entries(paymentLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Manzil</label>
                <input value={form.address} onChange={e => f('address', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>

              {/* Bank rekvizitlari */}
              <div className="md:col-span-2">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2 border-b pb-1">🏦 Bank rekvizitlari</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">INN</label>
                    <input value={form.inn} onChange={e => f('inn', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Bank nomi</label>
                    <input value={form.bank_name} onChange={e => f('bank_name', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Bank hisob raqami</label>
                    <input value={form.bank_account} onChange={e => f('bank_account', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">SWIFT</label>
                    <input value={form.swift} onChange={e => f('swift', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
                  </div>
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Izoh</label>
                <textarea value={form.notes} onChange={e => f('notes', e.target.value)} rows={3}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Bekor qilish</button>
              <button onClick={handleSubmit} disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">
                {loading ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-xl w-full md:max-w-md p-4 md:p-6">
            <h3 className="text-lg md:text-xl font-bold mb-4">{showDetail.company_name}</h3>
            <div className="space-y-2 text-sm">
              {[
                { label: 'Kontakt shaxs', value: showDetail.contact_person },
                { label: 'Telefon', value: showDetail.phone },
                { label: 'Email', value: showDetail.email },
                { label: 'Mamlakat', value: showDetail.country },
                { label: 'Shahar', value: showDetail.city },
                { label: 'Manzil', value: showDetail.address },
                { label: 'Valyuta', value: currencyLabels[showDetail.currency] },
                { label: "To'lov sharti", value: paymentLabels[showDetail.payment_terms] },
                { label: 'INN', value: showDetail.inn },
                { label: 'Bank nomi', value: showDetail.bank_name },
                { label: 'Bank hisob raqami', value: showDetail.bank_account },
                { label: 'SWIFT', value: showDetail.swift },
                { label: 'Izoh', value: showDetail.notes },
              ].map(item => item.value ? (
                <div key={item.label} className="flex justify-between border-b pb-2">
                  <span className="text-gray-500">{item.label}</span>
                  <span className="font-medium text-right">{item.value}</span>
                </div>
              ) : null)}
            </div>
            <div className="flex justify-end mt-4 gap-2">
              {canFull && <button onClick={() => { setShowDetail(null); openEdit(showDetail); }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">Tahrirlash</button>}
              <button onClick={() => setShowDetail(null)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Yopish</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Suppliers;
